<template>
<div>
	<CloToolbar reverse color="primary" class="Toolbar--species Toolbar--fixed" split size="md">
		<CloToolbarGroup divider="">
			<CloToolbarItem :label="$t('species.showingDataFor')" />
			<CloToolbarItem
				id="changeRegion"
				:label="currentRegionName"
				dropdown=""
				icon="region"
				:closeDropdownIncrement="closeDropdownIncrement"
				ref="changeRegionDropdown"
			>
				<CloSuggest
					:url="`${ebirdApiUrl}/ref/region/find?key=${ebirdApiKey}&q=%QUERY`"
					:label="$t('species.changeRegion')"
					hideLabel
					:placeholder="$t('choosePlace.placeHolder')"
					:minLength="2"
					@selection="changeRegion"
				/>
				<CloButton class="Button--link" @click.native="changeRegionToWorld">{{ $t('generic.World') }}</CloButton>
			</CloToolbarItem>
		</CloToolbarGroup>
		<CloToolbarGroup v-if="listContext" class="u-showForMedium">
			<CloToolbarItem :label="$t('species.list')" />
			<CloToolbarItem :href="listContext.url" :label="listContext.name" icon="list" />
		</CloToolbarGroup>
		<CloToolbarGroup align="right" bottom>
			<CloToolbarItem
				id="changeSpecies"
				:label="$t('species.changeSpecies')"
				dropdown=""
				align="right"
				:closeDropdownIncrement="closeDropdownIncrement"
			>
				<CloSuggest
					:url="`${ebirdApiUrl}/ref/taxon/find?cat=species&key=${ebirdApiKey}&locale=${sppLocale}&q=%QUERY&count=${speciesFinderConfig === 'all' ? '150' : '500'}`"
					:label="$t('species.changeSpecies')"
					hideLabel
					:placeholder="$t('checklist.EnterSppName')"
					:minLength="3"
					@selection="changeSpecies"
					:filter="listContext && speciesFinderConfig === 'list' ? listContext.speciesList : []"
				/>
				<div class="u-text-3 u-stack-md">
					<a :href="`${baseUrl}species/surprise-me`">{{ $t('search.surpriseMe') }}</a>
				</div>
				<template v-if="listContext">
					<CloInputRadio
						size="md"
						id="listSpecies"
						value="list"
						v-model="speciesFinderConfig"
						:label="`${$t('species.searchCurrentList')} (${listContext.name})`"
					/>
					<CloInputRadio
						size="md"
						id="allSpecies"
						value="all"
						v-model="speciesFinderConfig"
						:label="$t('species.searchAllSpecies')"
					/>
				</template>
			</CloToolbarItem>
			<CloToolbarItem v-if="previousCode" :href="`${baseUrl}species/${previousCode}${currentRegionCode ? '/' + currentRegionCode : ''}`" :label="previousSpeciesName" previous="" divider="" />
			<CloToolbarItem v-if="nextCode" :href="`${baseUrl}species/${nextCode}${currentRegionCode ? '/' + currentRegionCode : ''}`" :label="nextSpeciesName" next="" />
		</CloToolbarGroup>
  </CloToolbar>

<!-- Hero -->
<slot></slot>

<!-- Anchor for setting scroll position after region change -->
<a id="species-regional-data"></a>

<div class="Species-regionalData">
	<div class="Species-regionalData-stats">
		<CloHeading level="2" size="2" thin class="u-stack-md">{{ $t('species.stats') }}</CloHeading>
		<CloTable
			v-if="userId && stats.length > 0"
			class="u-stack-md"
			:caption="$t('species.stats')"
			:columns="columns"
			:tableData="stats"
			speciesStats
		/>
		<template v-if="!userId && stats.length > 0">
			<CloStatList class="StatsToolbar--species u-stack-md">
				<CloStatListItem class="Stat--white">
					<CloStat
						icon="check"
						:stat="stats[0][1].value"
						:label="$t('species.observations')"
					/>
				</CloStatListItem>
				<CloStatListItem class="Stat--white">
					<CloStat
						icon="photo"
						:stat="stats[0][2].value"
						:label="$t('species.obsWithPhotos')"
						:href="stats[0][2].href"
					/>
				</CloStatListItem>
				<CloStatListItem class="Stat--white">
					<CloStat
						icon="audio"
						:stat="stats[0][3].value"
						:label="$t('species.obsWithAudio')"
						:href="stats[0][3].href"
					/>
				</CloStatListItem>
			</CloStatList>
			<p class="u-text-3 u-textCenter u-margin-none" v-html="$t('species.seeYourStats', [`<a href='${loginUrl}'>`, `</a>`])"></p>
		</template>
		
	</div>
	<div class="Species-regionalData-barchart">
		<CloHeading level="2" size="2" thin class="u-stack-md">{{ $t('species.weeklyBarCharts') }}</CloHeading>
		<div class="Species-regionalData-barchart-container">
			<CloBarchart
				v-if="chartData.length > 0"
				:caption="$t('species.weeklyBarCharts')"
				:chartData="chartData"
			/>
			<p v-if="!currentRegionCode" 
				@click="focusOnChangeRegion"
				class="u-text-3 u-margin-none"
				v-html="$t('species.chooseRegionForBarChart', [
					`<a href='#'>`, `</a>`
				])"
			>
			</p>
		</div>
		
	</div>
	<div class="Species-regionalData-map">
		<div class="Species-regionalData-map-heading">
			<CloSectionHeading level="2" size="2" thin right class="u-margin-none">
				<template #heading>
					<CloHeading level="2" size="2" thin class="u-margin-none">{{ $t('species.rangeMap') }}</CloHeading>
				</template>
				<template #action>
					<a :href="largeMapUrl">{{ $t('species.largeMap' ) }}</a>
				</template>
			</CloSectionHeading>
		</div>
		<div v-clo-lazy-trigger="loadMap" class="Species-regionalData-map-container">
			<CloMapGoogle
				v-if="showMap"
				class="Species-regionalData-map-map"
				:googleMapsLibraryUrl="googleMapsLibraryUrl"
				:apiKey="googleApiKey"
				observations
				:gridSmall="gridSmall"
				:gridLarge="gridLarge"
				mapTypeId="roadmap"
				:dataBounds="dataBounds"
				:geoCacheUrl="geoCacheUrl"
				:gridSizeZoomThreshold="gridSizeZoomThreshold"
				:pointsZoomThreshold="23"
				@zoom_changed="zoomChanged"
			></CloMapGoogle>
			<div v-if="showMapDataLoading" class="Species-regionalData-map-loading" role="alert" aria-live="polite">
				<div>{{ $t('effortMap.creatingGrid', [gridScale]) }}</div>
				<CloLoadingAnimation class="LoadingAnimation--basic" />
			</div>
		</div>
		
	</div>
</div>
	<div class="Species-media-feeds">
		<CloSectionHeading level="2" size="2" thin class="u-stack-md">
			<template #heading>
				<CloHeading level="2" size="2" thin class="u-margin-none">{{ $t('species.media') }}</CloHeading>
			</template>
			<template #action v-if="userId">
				<div class="Species-media-actions">
					<CloButton 
						size="xs" 
						pill 
						class="Button--pill u-margin-none" 
						:class="{'Button--highlight': !myMedia, 'Button--hollow': myMedia }" 
						@click.native="myMedia = false"
					>
						{{ $t('species.allMedia') }}
					</CloButton>
					<CloButton 
						size="xs" 
						pill 
						class="Button--pill u-margin-none" 
						:class="{'Button--highlight': myMedia, 'Button--hollow': !myMedia }" 
						@click.native="myMedia = true"
					>
						{{ $t('species.myMedia') }}
					</CloButton>
				</div>
			</template>
		</CloSectionHeading>
		<div v-clo-lazy-trigger="loadMedia">
			<CloMediaFeed
				:list="photoList"
				:limit="14"
				:title="$t('species.topPhotos')"
				:filter="currentRegionName"
				:moreLink="morePhotosUrl"
				:moreLabel="$t('generic.viewAll')"
				dataFormat="search2.0"
				mediaVersion="2.0"
				:mediaDownloadUrl="mediaDownloadUrl"
				:ebirdWebUrl="ebirdWebUrl"
				:mediaSpecimenUrl="mediaSpecimenUrl"
				ratingEnabled
				:getRatingsUrl="getRatingsUrl"
				:saveRatingUrl="saveRatingUrl"
				:ratingCsrf="ratingCsrf"
			>
				<div v-if="!loadingPhotos">
					{{ $t('species.noPhotosAvailable') }}
					<a :href="`${baseUrl}submit`">{{ $t('species.submitYours') }}</a>
				</div>
				<CloLoadingAnimation v-else class="LoadingAnimation--basic" />
			</CloMediaFeed>
			<CloMediaFeed
				:list="audioList"
				:limit="14"
				:title="$t('species.topAudio')"
				:filter="currentRegionName"
				:moreLink="moreAudioUrl"
				:moreLabel="$t('generic.viewAll')"
				dataFormat="search2.0"
				mediaVersion="2.0"
				:mediaDownloadUrl="mediaDownloadUrl"
				:ebirdWebUrl="ebirdWebUrl"
				:mediaSpecimenUrl="mediaSpecimenUrl"
				ratingEnabled
				:getRatingsUrl="getRatingsUrl"
				:saveRatingUrl="saveRatingUrl"
				:ratingCsrf="ratingCsrf"
			>
				<div v-if="!loadingAudio">
					{{ $t('species.noAudioAvailable') }}
					<a :href="`${baseUrl}submit`">{{ $t('species.submitYours') }}</a>
				</div>
				<CloLoadingAnimation v-else class="LoadingAnimation--basic" />
			</CloMediaFeed>
			<CloMediaFeed
				:list="videoList"
				:limit="14"
				:title="$t('species.topVideo')"
				:filter="currentRegionName"
				:moreLink="moveVideosUrl"
				:moreLabel="$t('generic.viewAll')"
				dataFormat="search2.0"
				mediaVersion="2.0"
				:mediaDownloadUrl="mediaDownloadUrl"
				:ebirdWebUrl="ebirdWebUrl"
				:mediaSpecimenUrl="mediaSpecimenUrl"
				ratingEnabled
				:getRatingsUrl="getRatingsUrl"
				:saveRatingUrl="saveRatingUrl"
				:ratingCsrf="ratingCsrf"
			>
				<div v-if="!loadingVideo">
					{{ $t('species.noVideoAvailable') }}
				</div>
				<CloLoadingAnimation v-else class="LoadingAnimation--basic" />
			</CloMediaFeed>
	</div>
</div>
<CloLightbox
		v-if="lightboxOpen"
		reverse
		:dataFormat="lightboxDataFormat"
		mediaVersion="2.0"
		:source="lightboxSource"
		:list="lightboxData"
		:goTo="lightboxGoToAssetId"
		:mediaDownloadUrl="mediaDownloadUrl"
		:ebirdWebUrl="ebirdWebUrl"
		:mediaSpecimenUrl="mediaSpecimenUrl"
		@close="closeLightbox"
	></CloLightbox>
</div>
</template>

<script>
import axios from 'axios';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloSectionHeading from '@/layouts/CloSectionHeading/CloSectionHeading.vue';
import CloTable from '@/base/CloTable/CloTable.vue';
import CloMapGoogle from '@/maps/CloMapGoogle/CloMapGoogle.vue';
import CloBarchart from '@/visualizations/CloBarchart/CloBarchart.vue';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloMediaFeed from '@/media/CloMediaFeed/CloMediaFeed.vue';
import CloLightbox from '@/media/CloLightbox/CloLightbox.vue';
import CloToolbar from '@/navigation/CloToolbar/CloToolbar.vue';
import CloToolbarGroup from '@/navigation/CloToolbar/CloToolbarGroup.vue';
import CloToolbarItem from '@/navigation/CloToolbar/CloToolbarItem.vue';
import CloSuggest from '@/forms/CloSuggest/CloSuggest.vue';
import CloStatList from '@/visualizations/CloStatList/CloStatList.vue';
import CloStatListItem from '@/visualizations/CloStatList/CloStatListItem.vue';
import CloStat from '@/visualizations/CloStat/CloStat.vue';
import CloInputRadio from '@/forms/CloInputRadio/CloInputRadio.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';

export default ({
	name: 'SpeciesPage',
	components: {
		CloHeading,
		CloSectionHeading,
		CloTable,
		CloMapGoogle,
		CloBarchart,
		CloButton,
		CloMediaFeed,
		CloToolbar,
		CloToolbarGroup,
		CloToolbarItem,
		CloSuggest,
		CloStatList,
		CloStatListItem,
		CloStat,
		CloLightbox,
		CloInputRadio,
		CloLoadingAnimation,
	},
	props: {
		userId: {
			type: String,
			required: true,
		},
		speciesCode: {
			type: String,
			required: true
		},
		previousSpeciesCode: {
			type: String,
			required: true
		},
		nextSpeciesCode: {
			type: String,
			required: true
		},
		regionCode: {
			type: String,
			default: 'world'
		},
		regionName: {
			type: String,
			required: true
		},
		comName: {
			type: String,
			required: true
		},
		mediaUrl: {
			type: String,
			required: true
		},
		ebirdApiUrl: {
			type: String,
			required: true
		},
		ebirdApiKey: {
			type: String,
			required: true
		},
		mediaApiUrl: {
			type: String,
			required: true
		},
		googleMapsLibraryUrl: {
			type: String,
			required: true
		},
		googleApiKey: {
			type: String,
			required: true
		},
		loginUrl: {
			type: String,
			required: true
		},
		geoCacheUrl: {
			type: String,
			required: true
		},
		rsidUrl: {
			type: String,
			required: true
		},
		mapBoundsUrl: {
			type: String,
			required: true
		},
		baseUrl: {
			type: String,
			required: true
		},
		mediaDownloadUrl: {
			type: String,
			required: true
		},
		mediaSpecimenUrl: {
			type: String,
			required: true
		},
		getRatingsUrl: {
			type: String,
			required: true
		},
		saveRatingUrl: {
			type: String,
			required: true
		},
		ratingCsrf: {
			type: String,
			required: true
		},
		sppLocale: {
			type: String,
			required: true
		},
		locale: {
			type: String,
			required: true
		},
	},
	data() {
		return {
			ebirdWebUrl: this.baseUrl.slice(0, -1),
			currentRegionCode: this.regionCode,
			currentRegionName: this.regionName,
			previousCode: this.previousSpeciesCode,
			nextCode: this.nextSpeciesCode,
			previousSpeciesName: '',
			nextSpeciesName: '',
			chartData: [],
			showMap: false,
			gridSmall: null,
			gridLarge: null,
			// default bounds for species with no data
			dataBounds: {
				minY: -80,
				minX: -170,
				maxY: 80,
				maxX: 170,
			},
			columns: [
				{ label: this.$t('sightings.observer'), srOnly: true },
				{ label: this.$t('species.observations'), icon: 'check' },
				{ label: this.$t('species.obsWithPhotos'), icon: 'photo' },
				{ label: this.$t('species.obsWithAudio'), icon: 'audio' },
			],
			stats: [
				[
					{ value: this.$t('species.you'), header: true, highlight: true },
					{ value: '', loading: true, highlight: true },
					{ value: '', loading: true, highlight: true },
					{ value: '', loading: true, highlight: true }
				],
				[
					{ value: 'eBird', header: true },
					{ value: '', loading: true },
					{ value: '', loading: true },
					{ value: '', loading: true }
				]
			],
			photoAssets: window.photoAssetsJson,
			audioAssets: window.audioAssetsJson,
			loadingPhotos: true,
			loadingAudio: true,
			loadingVideo: true,
			photoList: [],
			audioList: [],
			videoList: [],
			morePhotosUrl: '',
			moreAudioUrl: '',
			moveVideosUrl: '',
			myMedia: false,
			closeDropdownIncrement: 0,
			mediaLoaded: false,
			mapLoaded: false,
			lightboxData: [],
			lightboxOpen: false,
			lightboxGoToAssetId: 0,
			lightboxDataFormat: 'gallery3.0',
			lightboxSource: 'gallery3.0',
			storageKey: 'ebird_species_guide',
			listContext: null,
			speciesFinderConfig: 'all',
			gridSizeZoomThreshold: 6,
			gridScale: 100,
			showMapDataLoading: false,
		};
	},
	computed: {
		largeMapUrl() {
			return `${this.baseUrl}map/${this.speciesCode}?env.minX=${this.dataBounds.minX}&env.minY=${this.dataBounds.minY}&env.maxX=${this.dataBounds.maxX}&env.maxY=${this.dataBounds.maxY}`;
		},
	},
	watch: {
		myMedia() {
			// refresh media feeds when switching between all and my media
			this.getMedia()
		}
	},
	mounted() {
		this.getListContext()
		this.setMediaData()
		this.getBarchartData()
		this.getStats()
		this.handleOpenLightbox()
		this.handleToolbarFocus()
		this.handleBowMedia()
	},
	methods: {
		handleToolbarFocus() {
			const buttons = document.querySelectorAll('[data-toolbar-toggler]')
			Array.prototype.forEach.call(buttons, (button) => {
				button.addEventListener('click', (e) => {
					const dropdown = e.target.closest('[data-toolbar-toggler]').nextElementSibling
					const input = dropdown.querySelector('input')
					if (input) {
						input.focus()
					}
				})
			})
		},
		loadMedia() {
			// lazy loaded with v-clo-lazy-trigger
			if (this.mediaLoaded) {
				return
			}
			this.mediaLoaded = true
			this.getMedia()
		},
		loadMap() {
			// lazy loaded with v-clo-lazy-trigger
			if (this.mapLoaded) {
				return
			}
			this.mapLoaded = true
			this.getRSID()
		},
		changeRegion(selection) {
			// handle selection from region finder
			this.currentRegionCode = selection.code
			this.currentRegionName = selection.name
			this.closeDropdownIncrement++
			this.handleChangeRegion()
		},
		changeRegionToWorld() {
			// click on "World" button
			this.currentRegionCode = ''
			this.currentRegionName = 'World'
			this.closeDropdownIncrement++
			this.handleChangeRegion()
		},
		handleChangeRegion() {
			// set URL
			this.setRegionPath()
			// clear list context when changing region
			this.clearListContext()
			// reset previous and next species codes
			this.previousCode = this.previousSpeciesCode
			this.nextCode = this.nextSpeciesCode
			this.getSpeciesNames()
			this.getBarchartData()
			this.getStats()
			this.getRSID()
			this.getMedia()
			// scroll to and focus on dynamic content
			this.$nextTick(() => {
				const speciesRegionalData = document.getElementById('species-regional-data')
				speciesRegionalData.focus()
				speciesRegionalData.scrollIntoView()
			})
		},
		setRegionPath() {
			// set region code in URL, do not push into history
			let url = window.location.pathname
			const paths = url.split('/')
			const speciesPathIndex = paths.findIndex((path) => path === 'species')
			if (speciesPathIndex + 2 > paths.length) {
				url += `/${this.currentRegionCode}`
			} else {
				if (this.currentRegionCode === '') {
					paths.splice(speciesPathIndex + 2, 1)
				} else {
					paths[speciesPathIndex + 2] = this.currentRegionCode
				}
				url = paths.join('/')
			}
			window.history.replaceState({}, '', url)
		},
		changeSpecies(selection) {
			// handle selection from species finder
			if (this.currentRegionCode) {
				window.location.href = `${baseUrl}species/${selection.code}/${this.currentRegionCode}`
			} else {
				window.location.href = `${baseUrl}species/${selection.code}`
			}
		},
		getSpeciesNames() {
			// get names for prev/next species codes if set
			const url = `${this.ebirdApiUrl}/ref/taxonomy/ebird/`
			const speciesCodes = [this.previousCode, this.nextCode].filter((code) => code !== '').join(',')
			axios
			.get(url, {
					headers: { 'X-eBirdApiToken': this.ebirdApiKey },
					params: {
						fmt: 'json',
						species: speciesCodes,
						locale: this.sppLocale
					}
				})
			.then((res) => {
				if (this.previousCode !== '') {
					this.previousSpeciesName = res.data.find((sp) => sp.speciesCode === this.previousCode).comName
				}
				if (this.nextCode !== '') {
					this.nextSpeciesName = res.data.find((sp) => sp.speciesCode === this.nextCode).comName
				}
			})
			.catch((err) => {
				console.log(err);
			});
		},
		focusOnChangeRegion(e) {
			// "Choose a region for bar chart" link
			if (e.target.tagName !== 'A') {
				return
			}
			e.preventDefault()
			const changeRegion = document.getElementById('changeRegion')
			changeRegion.setAttribute('aria-hidden', 'false')
			this.$nextTick(() => {
				const suggest = changeRegion.querySelector('input')
				suggest.focus()
			})
		},
		getListContext() {
			// if coming from a list, get the list context from localStorage and copy it to 
			// otherwise, get it from sessionStorage
			let listContext = localStorage.getItem(this.storageKey)
			if (listContext) {
				sessionStorage.setItem(this.storageKey, listContext)
				localStorage.removeItem(this.storageKey)
			} else {
				listContext = sessionStorage.getItem(this.storageKey)
			}
			// handle list context
			if (listContext) {
				this.listContext = JSON.parse(listContext)
				// find current species
				const currentSpeciesIndex = this.listContext.speciesList.findIndex((code) => code === this.speciesCode)
				if (currentSpeciesIndex === -1) {
					// not found in list, so clear list context
					this.clearListContext()
				} else {
					// set previous and next species codes
					if (currentSpeciesIndex > 0) {
						this.previousCode = this.listContext.speciesList[currentSpeciesIndex - 1]
					} else {
						this.previousCode = ''
					}
					if (currentSpeciesIndex < this.listContext.speciesList.length - 1) {
						this.nextCode = this.listContext.speciesList[currentSpeciesIndex + 1]
					} else {
						this.nextCode = ''
					}
					// set species finder config=
					this.speciesFinderConfig = 'list'
				}
				// replace certain html entities in list name
				let name = this.listContext.name.replaceAll('&ndash;', '-')
				name = name.replaceAll('&lt;', ' ')
				name = name.replaceAll('&gt;', ' ')
				name = name.replaceAll('&#034;', ' ')
				this.listContext.name = name
				// set region filter if not world or null
				if (this.listContext.regionCode && this.listContext.regionCode !== 'world') {
					this.currentRegionCode = this.listContext.regionCode
					this.setRegionPath()
				}
			}
			this.getSpeciesNames()
		},
		clearListContext() {
			sessionStorage.removeItem(this.storageKey)
			this.listContext = null
		},
		async getBarchartData() {
			if (!this.currentRegionCode) {
				this.chartData = []
				return
			}
			
			// use API
			const url = `${this.ebirdApiUrl}/product/barchart`
			const params = {
				spp: this.speciesCode,
				regionCodes: this.currentRegionCode,
			}
			const headers = {
				'X-eBirdApiToken': this.ebirdApiKey,
			}
			try {
				const { data } = await axios.get(url, { params, headers })

				// prepare data for Lichen CloBarchart component
				const values = data.dataRows[0].values
				const buckets = data.dataRows[0].buckets

				const chartData = buckets.map((bucket, i) => {
					const bar = bucket === -1 ? bucket : (100 * (bucket / 9)).toFixed(0)
					const freq = values[i] === 0 ? '0' : values[i] < 0.001 ? '<0.1' : (values[i] * 100).toFixed(1)
					return { bar, freq }
				})

				this.chartData = chartData
			} catch (err) {
				console.log(err)
			}
		},
		async getRegionInfo() {
			// get region name and region bounds
			const url = `${this.ebirdApiUrl}/ref/region/info/${this.currentRegionCode}`
			const headers = {
				'X-eBirdApiToken': this.ebirdApiKey,
			}
			try {
				const { data } = await axios.get(url, { headers })
				// set bounds for map
				this.dataBounds = data.bounds
				// set region name (only needed when coming from a list with regionCode)
				this.currentRegionName = data.result
				this.$nextTick(() => {
					this.showMap = true
				})
			} catch (err) {
				console.log(err)
			}
		},
		getStats() {
			// get media stats
			let regionParam = ''
			const params = {
				speciesCode: this.speciesCode
			}
			if (this.userId) {
				params.userId = this.userId
			}
			if (this.currentRegionCode) {
				params.regionCode = this.currentRegionCode
				regionParam = `&regionCode=${this.currentRegionCode}`
			}
			axios
			.get(this.mediaApiUrl + 'stats/obs', { params })
			.then((res) => {
				let ebirdRow = 0
				if (this.userId) {
					this.stats[0][2].loading = false
					this.stats[0][3].loading = false
					this.stats[0][2].value = this.$formatNumber(res.data.userCountsWithPhoto)
					this.stats[0][3].value = this.$formatNumber(res.data.userCountsWithAudio)
					this.stats[0][2].href = `${this.mediaUrl}?birdOnly=true&userId=${this.userId}&taxonCode=${this.speciesCode}&mediaType=photo${regionParam}`
					this.stats[0][3].href = `${this.mediaUrl}?birdOnly=true&userId=${this.userId}&taxonCode=${this.speciesCode}&mediaType=audio${regionParam}`
					ebirdRow = 1
				}
				this.stats[ebirdRow][2].loading = false
				this.stats[ebirdRow][3].loading = false
				this.stats[ebirdRow][2].value = this.$formatNumber(res.data.countsWithPhoto)
				this.stats[ebirdRow][3].value = this.$formatNumber(res.data.countsWithAudio)
				this.stats[ebirdRow][2].href = `${this.mediaUrl}?birdOnly=true&taxonCode=${this.speciesCode}&mediaType=photo${regionParam}`
				this.stats[ebirdRow][3].href = `${this.mediaUrl}?birdOnly=true&taxonCode=${this.speciesCode}&mediaType=audio${regionParam}`

			})
			.catch((err) => {
				console.log(err);
			});

			// get eBird stats
			let ebirdRegionParam = ''
			let ebirdRegionCode = ''
			const eBirdParams = {}
			if (this.userId) {
				const username = btoa(this.userId.replace('USER', ''));
				eBirdParams.username = username
			}
			if (this.currentRegionCode) {
				ebirdRegionCode = this.currentRegionCode
				ebirdRegionParam = `&r=${this.currentRegionCode}`
			}
			axios
			.get(`${this.ebirdApiUrl}/product/obsstats/${this.speciesCode}/${ebirdRegionCode}`, {
					params : eBirdParams,
					headers: { 'X-eBirdApiToken': this.ebirdApiKey },
				})
			.then((res) => {
				let ebirdRow = 0
				if (this.userId) {
					this.stats[0][1].loading = false
					this.stats[0][1].value = this.$formatNumber(res.data.userObsCount)
					this.stats[0][1].href = `${this.baseUrl}lifelist?spp=${this.speciesCode}${ebirdRegionParam}`
					ebirdRow = 1
				}
				this.stats[ebirdRow][1].loading = false
				this.stats[ebirdRow][1].value = this.$formatNumber(res.data.obsCount)
			})
			.catch((err) => {
				console.log(err);
			});
		},
		zoomChanged(info) {
			// 100k grid or 20k grid based on zoom level
			if (info.zoom < this.gridSizeZoomThreshold) {
				this.gridScale = 100
				if (!this.gridLarge) {
					this.getRSID(false)
				}
			} else {
				this.gridScale = 20
				if (!this.gridSmall) {
					this.getRSID(false)
				}
			}
		},
 		getRSID(firstTime = true) {
			// get result set IDs used to load tiles on map
			if (this.showMapDataLoading) {
				return
			}
			this.showMapDataLoading = true
			axios
			.get(this.rsidUrl, {
					params: {
						speciesCode: this.speciesCode,
						gridScale: this.gridScale, // 100k grid or 20k grid
					},
				})
			.then((res) => {
				if (this.gridScale === 100) {
					this.gridLarge = res.data
				} else {
					this.gridSmall = res.data
				}
				if (firstTime) {
					if (this.currentRegionCode) {
						this.getRegionInfo()
					} else {
						this.getBounds()
					}
				}
				this.showMapDataLoading = false
			})
			.catch((err) => {
				console.log(err);
			});
		
		},
		getBounds() {
			// get lat/lng bounds of species range
			axios
			.get(this.mapBoundsUrl, {
					params: {
						speciesCode: this.speciesCode,
						rsid: this.gridLarge,
					},
				})
			.then((res) => {
				if (res.data) {
					this.dataBounds = res.data
				}
				this.$nextTick(() => {
					this.showMap = true
				})
			})
			.catch((err) => {
				console.log(err);
			});
		},
		getMedia() {
			// get top photos, audio, and videos
			const userParam = this.myMedia ? `&userId=${this.userId}` : ''
			const regionParam = this.currentRegionCode ? `&regionCode=${this.currentRegionCode}` : ''
			this.morePhotosUrl = `${this.mediaUrl}?birdOnly=true&taxonCode=${this.speciesCode}&mediaType=photo${userParam}${regionParam}&sort=rating_rank_desc`
			this.moreAudioUrl = `${this.mediaUrl}?birdOnly=true&taxonCode=${this.speciesCode}&mediaType=audio${userParam}${regionParam}&sort=rating_rank_desc`
			this.moveVideosUrl = `${this.mediaUrl}?birdOnly=true&taxonCode=${this.speciesCode}&mediaType=video${userParam}${regionParam}&sort=rating_rank_desc`
			this.loadingPhotos = true
			this.loadingAudio = true
			this.loadingVideo = true
			const params = {
				taxonCode: this.speciesCode,
				sort: 'rating_rank_desc',
				count: 14,
				taxaLocale: this.sppLocale,
			}
			if (this.myMedia) {
				params.userId = this.userId
			}
			if (this.currentRegionCode) {
				params.regionCode = this.currentRegionCode
			}
			const photoParams = Object.assign(params, { mediaType: 'photo' })
			axios
			.get(this.mediaApiUrl + 'search', {
					params: photoParams
			})
			.then((res) => {
				this.loadingPhotos = false
				this.photoList = res.data
				this.triggerResize()
			})
			.catch((err) => {
				console.log(err);
				this.loadingPhotos = false
				this.photoList = []
			});
			const audio = Object.assign(params, { mediaType: 'audio' })
			axios
			.get(this.mediaApiUrl + 'search', {
					params: audio
			})
			.then((res) => {
				this.loadingAudio = false
				this.audioList = res.data
				this.triggerResize()
			})
			.catch((err) => {
				console.log(err);
				this.loadingAudio = false
				this.audioList = []
			});
			const videoParams = Object.assign(params, { mediaType: 'video' })
			axios
			.get(this.mediaApiUrl + 'search', {
					params: videoParams
			})
			.then((res) => {
				this.loadingVideo = false
				this.videoList = res.data
				this.triggerResize()
			})
			.catch((err) => {
				console.log(err);
				this.loadingVideo = false
				this.videoList = []
			});
		
		},
		setMediaData() {
			// configure lightbox for hero photo gallery or fallback to top search result
			// fix locale for gallery titles and captions
			if (this.photoAssets && this.photoAssets.galleryAssets) {
				// from gallery
				this.lightboxDataFormat = 'gallery3.0'
				this.lightboxSource = 'gallery3.0'
				// fix for photoAssets JSON sometimes using cache of wrong locale for titles
				this.photoAssets.galleryAssets.forEach((asset) => {
					if (asset.titlesMap && this.locale in asset.titlesMap) {
						asset.title = asset.titlesMap[this.locale].localeText
					} else {
						const lang = this.locale.split('_')[0]
						if (asset.titlesMap && lang in asset.titlesMap) {
							asset.title = asset.titlesMap[lang].localeText
						}
					}
					// these captions are not intended for the eBird species page
					asset.caption = ''
				})
				this.lightboxData = this.photoAssets.galleryAssets
			} else if (this.photoAssets && this.photoAssets.results) {
				// from search results
				this.lightboxDataFormat = 'search'
				this.lightboxSource = 'search'
				this.lightboxData = this.photoAssets.results.content
			}
			if (this.audioAssets && this.audioAssets.galleryAssets) {
				// from gallery
					// fix for audioAssets JSON sometimes using cache of wrong locale for titles
					this.audioAssets.galleryAssets.forEach((asset) => {
					if (asset.titlesMap && this.locale in asset.titlesMap) {
						asset.title = asset.titlesMap[this.locale].localeText
					} else {
						const lang = this.locale.split('_')[0]
						if (asset.titlesMap && lang in asset.titlesMap) {
							asset.title = asset.titlesMap[lang].localeText
						}
					}
					asset.caption = ''
				})
			} else if (this.audioAssets && this.audioAssets.results) {
				// from search results
			}
		},
		handleOpenLightbox() {
			const lightboxButtons = document.querySelectorAll('button[data-asset-id]')
			lightboxButtons.forEach((lightboxButton) => {
				lightboxButton.addEventListener('click', (e) => {
					e.preventDefault()
					this.lightboxGoToAssetId = e.target.closest('[data-asset-id]').getAttribute('data-asset-id')
					this.$nextTick(() => {
						this.lightboxOpen = true
					})
				})
				
			})
		},
		closeLightbox() {
			this.lightboxOpen = false
			const opener = document.querySelector(`button[data-asset-id="${this.lightboxGoToAssetId}"]`)
			if (opener) {
				opener.focus()
			}
		},
		triggerResize() {
			// this shouldn't be necessary, but was needed for CloMediaFeed to render properly
			this.$nextTick(() => {	
				const event = new CustomEvent('lichen:resize', { detail: { width: window.innerWidth } });
				document.dispatchEvent(event);
			})
		},
		handleBowMedia() {
			const media = document.getElementById('bow-media')
			if (!media) return
			const image = media.querySelector('img')
			if (!image) return
			image.addEventListener('error', () => {
				media.classList.add('Media--noContent')
			})

		}
	}
});
</script>

<style lang="scss">
// Styles for JSP templates (hero, identification, ads)

.Species-media-image {
	// force smaller photos to fill space, even if it causes the top and bottom to be cut off
	width: 100% !important;
}
.Species-media-button {
	// fix for Firefox
	width: 100%;
}
.Species-media-credit {
	display: flex;
	gap: 0 $space-md;
	flex-wrap: wrap;
	&--bold {
		font-weight: bold;
	}
	&:empty {
		display: none;
	}
}
.CarouselResponsive--showcase .CarouselResponsive-slide .Media-caption {
	@include text(1);
}
.Species-media-more {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: $color-overlay-black;
	display: grid;
	align-items: center;
	justify-content: center;
	@include text(5);
	color: $color-white;
	pointer-events: none;
}
button[data-asset-id] {
	cursor: pointer;
}
.Species-media-background {
	background-color: $color-neutral-5 !important;
}
.Species-media-empty {
	aspect-ratio: 5 / 3;
	display: grid;
	align-items: center;
	justify-content: center;
	gap: $space-md;
	@include colorReverse;
}
.Species-identification {
	@include inset-responsive;
	@include breakpoint(medium) {
		display: grid;
		grid-template-columns: auto 1fr;
		gap: $space-xl;
	}
	.Species-identification-heading {
		@include visuallyHidden;
		@include breakpoint(medium) {
			@include overrideVisuallyHidden;
		}
	}
	.Species-identification-audio {
		float: right;
		[dir='rtl'] & {
			float: left;
		}
		margin-inline-start: $space-md;
		margin-block-end: $space-md;
	}
}
.Advertisement--white {
	background-color: $color-white !important;
}
.Page-section--border {
	border-bottom-color: $color-neutral-2 !important;
}

// styles for Vue templates

.Species-regionalData {
	display: grid;
	gap: $space-md;
	grid-template-areas:
		"stats"
		"barchart"
		"map";
	@include breakpoint(medium) {
		grid-template-columns: 1fr 1fr;
		grid-template-areas:
			"stats map"
			"barchart map";
	}
	.Species-regionalData-stats {
		grid-area: stats;
		background-color: $color-primary-1;
		@include inset-md;
		@include breakpoint(medium) {
			@include inset-squish-lg;
		}
	}
	.Species-regionalData-barchart {
		grid-area: barchart;
		background-color: $color-primary-1;
		@include inset-md;
		@include breakpoint(medium) {
			@include inset-squish-lg;
		}
		.Species-regionalData-barchart-container {
			@include inset-md;
			background-color: $color-white;
		}
	}
	.Species-regionalData-map {
		grid-area: map;
		background-color: $color-primary-1;
		.Species-regionalData-map-heading {
			@include inset-md;
			@include breakpoint(medium) {
				@include inset-squish-lg;
			}
		}
		.Species-regionalData-map-map:before {
			padding-top: 62.5%
		}

		.Species-regionalData-map-container {
			position: relative;
			aspect-ratio: 16 / 9;
			background-color: $brand-color-ocean-blue-4;
		}
		.Species-regionalData-map-loading {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			background-color: $color-white;
			@include inset-md;
			@include dropShadow(2);
			display: flex;
			flex-direction: column;
			align-items: center;
			border-radius: $space-sm;
		}
	}
}

.Species-media-feeds {
	@include inset-md;
	@include breakpoint(medium) {
		@include inset-squish-lg;
	}
	.Species-media-actions {
		display: flex;
		gap: $space-sm;
	}
}
.Table--speciesStats {
	th {
		color: $color-text;
	}
	td {
		@include text(5);
		@include breakpoint(medium) {
			@include text(6);
		}
		font-weight: normal;
	}
}
.Toolbar.Toolbar--species {
	.Toolbar-group--fixedToBottom {
		background-color: $color-primary-6;
		.Toolbar-item-dropdown {
			margin-top: 65px;
			@include breakpoint(medium) {
				margin-top: 0;
			}
		}
		.Toolbar-item-button-label span {
			@include visuallyHidden;
			@include breakpoint(medium) {
				@include overrideVisuallyHidden;
			}
		}
		.Toolbar-item--hasDropdown {
			flex: 1;
		}
	}
	.Toolbar-item-dropdown {
		overflow-y: visible;
		padding-top: $space-md;
	}
}
.StatsToolbar--species {
	grid-template-columns: auto auto auto;
	.Stat--white {
		display: flex;
		padding: $space-md !important;
		background-color: $color-white;
	}
}
#species-regional-data {
	position: relative;
	top: -4rem;
}
</style>
